<template>
  <el-dialog
    title="Thông tin chữ ký số"
    :visible.sync="visible"
    width="800px"
    :close-on-click-modal="false"
    class="signature-dialog"
  >
    <div v-loading="loading">
      <template v-if="error">
        <el-alert
          :title="error"
          type="error"
          :closable="false"
          show-icon
        ></el-alert>
      </template>
      
      <div class="document-info" v-else>
        <h3>Thông tin tài liệu</h3>
        <el-table
          :data="[metadata]"
          style="width: 100%"
          border
          class="info-table"
        >
          <el-table-column prop="title" label="Tiêu đề" min-width="200">
            <template slot-scope="scope">
              {{ scope.row.title }}
            </template>
          </el-table-column>
          <el-table-column prop="author" label="Tác giả" min-width="150">
            <template slot-scope="scope">
              {{ scope.row.author }}
            </template>
          </el-table-column>
          <el-table-column prop="creator" label="Phần mềm tạo" min-width="150">
            <template slot-scope="scope">
              {{ scope.row.creator }}
            </template>
          </el-table-column>
          <el-table-column prop="creationDate" label="Ngày tạo" min-width="150">
            <template slot-scope="scope">
              {{ scope.row.creationDate }}
            </template>
          </el-table-column>
          <el-table-column label="Số trang" width="100">
            <template slot-scope="scope">
              {{ totalPages }}
            </template>
          </el-table-column>
        </el-table>

        <div class="signature-info" v-if="signatureInfo.length > 0">
          <h3>Thông tin chữ ký số</h3>
          <el-table
            :data="signatureInfo"
            style="width: 100%"
            border
            class="info-table"
          >
            <el-table-column type="index" label="STT" width="70" align="center"></el-table-column>
            <el-table-column prop="name" label="Tên chữ ký" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.name }}
              </template>
            </el-table-column>
            <el-table-column prop="signedBy" label="Người ký" min-width="150">
              <template slot-scope="scope">
                <el-tag 
                  :type="scope.row.signedBy !== 'Không xác định' ? 'success' : 'warning'"
                  class="signed-by"
                >
                  {{ scope.row.signedBy }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="signedDate" label="Thời gian ký" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.signedDate }}
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="Lý do" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.reason }}
              </template>
            </el-table-column>
            <el-table-column prop="location" label="Vị trí" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.location }}
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div v-else class="no-signature">
          <el-alert
            title="Không tìm thấy chữ ký số trong tài liệu"
            type="warning"
            :closable="false"
            show-icon
          ></el-alert>
          
          <div class="signature-help">
            <h4>Lưu ý về kiểm tra chữ ký số:</h4>
            <ul>
              <li>Chỉ xác minh được một số định dạng chữ ký số phổ biến.</li>
              <li>Nếu file PDF được ký số nhưng không hiển thị thông tin, hãy thử các phần mềm chuyên dụng như Adobe Acrobat.</li>
              <li>Không phải tất cả file PDF đều có chữ ký số.</li>
              <li>Định dạng chữ ký số có thể khác nhau tùy thuộc vào phần mềm ký và nhà cung cấp chứng thư số.</li>
            </ul>
            
            <h4>Thông tin file:</h4>
            <p><strong>Tên file:</strong> {{ file ? file.name : 'Không xác định' }}</p>
            <p><strong>Kích thước:</strong> {{ formatFileSize(file ? file.size : null) }}</p>
            <p><strong>Loại file:</strong> {{ file ? file.type : 'application/pdf' }}</p>
            
            <p class="signature-warning">
              <i class="el-icon-warning-outline"></i> 
              Nếu bạn chắc chắn rằng file đã được ký số, vui lòng sử dụng phần mềm ký số để kiểm tra lại.
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('update:visible', false)">Đóng</el-button>
    </span>
  </el-dialog>
</template>

<script>
import PdfSignatureService from '@/services/PdfSignatureService';
import { PDFDocument } from 'pdf-lib';

export default {
  name: 'SignatureVerificationDialog',
  
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    file: {
      type: File,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      metadata: {
        title: 'Không có tiêu đề',
        author: 'Không xác định',
        creator: 'Không xác định',
        creationDate: 'Không xác định',
        modificationDate: 'Không xác định'
      },
      signatureInfo: [],
      totalPages: 0,
      noSignatureMode: false
    };
  },

  watch: {
    visible(newVisible) {
      if (!newVisible) {
        this.noSignatureMode = false;
      } else if (this.file) {
        if (!this.noSignatureMode) {
          this.verifySignature();
        }
      }
    },
    file(newFile) {
      if (newFile && this.visible && !this.noSignatureMode) {
        this.verifySignature();
      }
    }
  },

  methods: {
    setNoSignatureMode() {
      this.noSignatureMode = true;
      this.loading = false;
      this.error = null;
      this.signatureInfo = [];
      
      // Giữ nguyên thông tin metadata từ file PDF
      this.metadata = {
        title: this.file ? this.file.name : 'Không có tiêu đề',
        author: 'Không xác định',
        creator: 'Không xác định',
        creationDate: 'Không xác định',
        modificationDate: 'Không xác định'
      };

      // Đọc thông tin cơ bản từ file PDF
      if (this.file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const arrayBuffer = e.target.result;
            const pdfDoc = await PDFDocument.load(arrayBuffer);

            console.log(pdfDoc);
            
            // Cập nhật metadata với thông tin từ PDF
            this.metadata = {
              title: pdfDoc.getSubject() || 'Không xác định',
              author: pdfDoc.getAuthor() || 'Không xác định',
              creator: pdfDoc.getProducer() || 'Không xác định',
              creationDate: pdfDoc.getCreationDate() ? new Date(pdfDoc.getCreationDate()).toLocaleString() : 'Không xác định',
              modificationDate: pdfDoc.getModificationDate() ? new Date(pdfDoc.getModificationDate()).toLocaleString() : 'Không xác định'
            };
            
            // Lấy số trang
            this.totalPages = pdfDoc.getPageCount();
          } catch (error) {
            console.error('Error reading PDF metadata:', error);
          }
        };
        reader.readAsArrayBuffer(this.file);
      }
    },

    async verifySignature() {
      if (!this.file || this.noSignatureMode) return;
      
      this.loading = true;
      this.error = null;

      try {
        const result = await PdfSignatureService.verifySignature(this.file);
        this.metadata = result.metadata;
        this.signatureInfo = result.signatureInfo;
        this.totalPages = result.totalPages;
      } catch (error) {
        console.error('Error verifying signature:', error);
        this.error = error.message || 'Có lỗi khi kiểm tra chữ ký số';
      } finally {
        this.loading = false;
      }
    },
    
    formatFileSize(bytes) {
      if (!bytes) return 'Không xác định';
      
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      
      return `${size.toFixed(2)} ${units[unitIndex]}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.signature-dialog {
  .document-info,
  .signature-info {
    margin-bottom: 20px;

    h3 {
      margin: 0 0 15px;
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }
  }

  .info-table {
    margin-bottom: 20px;
    table-layout: fixed;

    /deep/ .el-table__header {
      th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 500;
      }
    }

    /deep/ .el-tag {
      &.el-tag--success {
        background-color: #f0f9eb;
      }
      &.el-tag--warning {
        background-color: #fdf6ec;
      }
      &.el-tag.signed-by{
        white-space: unset;
        height: auto;
      }
    }
  }

  .no-signature {
    margin: 20px 0;
    
    .signature-help {
      background-color: #f8f8f8;
      border-radius: 4px;
      padding: 15px;
      margin-top: 15px;
      
      h4 {
        font-size: 15px;
        margin: 0 0 10px;
        color: #303133;
      }
      
      ul {
        margin: 0 0 15px;
        padding-left: 20px;
        
        li {
          margin-bottom: 5px;
          color: #606266;
        }
      }
      
      p {
        margin: 5px 0;
        color: #606266;
      }
      
      .signature-warning {
        color: #e6a23c;
        margin-top: 15px;
        font-weight: 500;
        
        i {
          margin-right: 5px;
        }
      }
    }
  }

  /deep/ .el-dialog__body {
    padding: 20px;
  }

  /deep/ .el-dialog__header {
    padding: 15px 20px;
    border-bottom: 1px solid #e8e8e8;
    
    .el-dialog__title {
      font-size: 16px;
      font-weight: 500;
    }
  }

  /deep/ .el-dialog__footer {
    padding: 15px 20px;
    border-top: 1px solid #e8e8e8;
  }
}
</style> 